<template>
  <div>
    <ui-modal-alert
      v-model="opened"
      modal-classes="location-qr-codes-download-modal"
      :apply-disabled="isDownloadDisabled"
      :is-loading="loading.modal || loading.download"
      :title="$t('checkinSettings.download.modalTitle')"
      :apply-button="$t('checkinSettings.download.download')"
      :close-button="$t('form.buttons.cancel')"
      no-apply-close
      @apply="handleDownload"
      @cancel="handleCancel"
      @closed="resetState"
    >
      <p class="text-muted mb-3">
        {{ $t('checkinSettings.download.description') }}
      </p>

      <ui-input
        v-model="pattern"
        :placeholder="$t('checkinSettings.download.search')"
        input-icon="search"
        class="mb-3"
      />

      <div
        v-if="loading.content && !items.length"
        class="location-qr-codes-download-modal__list location-qr-codes-download-modal__list--loading"
      >
        <ui-loading inline />
      </div>

      <div
        v-else-if="!items.length"
        class="location-qr-codes-download-modal__empty"
      >
        <ui-icon
          name="search-in-list"
          size="24"
          class="mb-2"
        />
        <p class="d-flex flex-column align-items-center text-grey text-center font-16 mb-0">
          <span class="text-black mb-2">
            {{ $t('checkinSettings.download.emptyTitle') }}
          </span>
          <span v-html="$t('checkinSettings.download.emptyDescription')" />
        </p>
      </div>

      <div
        v-else
        ref="list"
        class="location-qr-codes-download-modal__list"
      >
        <div class="location-qr-codes-download-modal__item">
          <ui-checkbox
            class="location-qr-codes-download-modal__checkbox"
            :checked="selectAll"
            :disabled="!total && !selectAll"
            @input="toggleAll"
          >
            <span class="location-qr-codes-download-modal__name">
              {{ $t('checkinSettings.download.all') }}
            </span>
          </ui-checkbox>
        </div>

        <div
          v-for="code in items"
          :key="code.id"
          class="location-qr-codes-download-modal__item"
        >
          <ui-checkbox
            class="location-qr-codes-download-modal__checkbox"
            v-model="selectedIds"
            :value="code.id"
            :disabled="selectAll"
          >
            <span class="location-qr-codes-download-modal__name">
              {{ code.qrName || code.id }}
            </span>
          </ui-checkbox>

          <button
            type="button"
            class="location-qr-codes-download-modal__preview"
            :disabled="loading.modal"
            @click="openPreview(code)"
          >
            <span>{{ $t('checkinSettings.download.view') }}</span>
            <ui-icon
              name="qr-code"
              size="20px"
            />
          </button>
        </div>

        <div
          v-if="loading.content"
          class="d-flex justify-content-center my-3"
        >
          <ui-loading inline />
        </div>
      </div>
    </ui-modal-alert>

    <location-qr-code-preview-modal
      v-model="previewOpened"
      :workplace-id="workplaceId"
      :code="previewCode"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { debounce } from 'lodash';
import api from '@/api';
import downloadFile from '@/utils/download';
import LocationQrCodePreviewModal from '@/components/Location/LocationQrCodePreviewModal.vue';

const PAGE_SIZE = 10;

export default {
  name: 'LocationQrCodesDownloadModal',

  model: {
    prop: 'show',
    event: 'input',
  },

  components: {
    LocationQrCodePreviewModal,
  },

  props: {
    show: Boolean,
    workplaceId: {
      type: [String, Number],
      required: true,
    },
  },

  data: () => ({
    items: [],
    selectedIds: [],
    selectAll: false,
    pattern: '',
    page: 1,
    total: 0,
    previewCode: null,
    listEl: null,
    hasMore: false,
    searchHandle: null,
    loading: {
      modal: false,
      content: false,
      download: false,
    },
  }),

  computed: {
    opened: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    previewOpened: {
      get() {
        return Boolean(this.previewCode);
      },
      set(value) {
        if (!value) {
          this.previewCode = null;
        }
      },
    },
    isDownloadDisabled() {
      if (this.loading.modal || this.loading.download) {
        return true;
      }

      if (this.selectAll) {
        return this.total === 0 && !this.items.length;
      }

      return this.selectedIds.length === 0;
    },
  },

  watch: {
    show(value) {
      if (value) {
        this.loadCodes({ reset: true, entity: 'modal' });
      }
    },
    pattern() {
      if (!this.show) {
        return;
      }

      this.loading.content = true;
      this.items = [];
      this.hasMore = false;
      this.searchCodes();
    },
  },

  methods: {
    searchCodes: debounce(function searchCodesDebounced() {
      if (!this.show) {
        return;
      }

      this.loadCodes({ reset: true, entity: 'content' });
    }, 500),
    toggleAll(checked) {
      this.selectAll = checked;

      if (checked) {
        this.selectedIds = [];
      }
    },
    openPreview(code) {
      this.previewCode = {
        id: code.id,
        qrName: code.qrName || code.id,
      };
    },
    async handleDownload() {
      if (this.isDownloadDisabled) {
        return;
      }

      this.loading.download = true;

      try {
        const { data, filename } = await api.qrCheckin.downloadCodesZip({
          workplaceId: this.workplaceId,
          ids: this.selectAll ? [] : this.selectedIds,
          all: this.selectAll,
        });

        if (!data?.size) {
          return;
        }

        const name = (filename || '')
          .replace(/["']/g, '')
          .split(';')[0]
          .trim() || 'QRCodes.zip';

        downloadFile(data, name);
        this.opened = false;
      } catch (e) {
        this.$showServerError(e);
      } finally {
        this.loading.download = false;
      }
    },
    handleCancel() {
      this.opened = false;
    },
    resetState() {
      if (this.searchHandle) {
        this.searchHandle.cancel();
      }

      this.items = [];
      this.selectedIds = [];
      this.selectAll = false;
      this.pattern = '';
      this.page = 1;
      this.total = 0;
      this.hasMore = false;
      this.previewCode = null;
      this.searchHandle = null;
      this.loading.modal = false;
      this.loading.content = false;
      this.loading.download = false;
    },
    bindListScroll() {
      const { list } = this.$refs;

      if (!list || list === this.listEl) {
        return;
      }

      if (this.listEl) {
        this.listEl.removeEventListener('scroll', this.onListScroll);
      }

      this.listEl = list;
      this.listEl.addEventListener('scroll', this.onListScroll);
    },
    onListScroll(e) {
      const el = e.currentTarget;
      const reachedBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 24;

      if (!reachedBottom || !this.hasMore || this.loading.content || this.loading.modal) {
        return;
      }

      this.page += 1;
      this.loadCodes({ reset: false, entity: 'content' });
    },
    async loadCodes({ reset, entity }) {
      if (!this.show) {
        return;
      }

      if (this.searchHandle) {
        this.searchHandle.cancel();
      }

      this.loading[entity] = true;

      if (reset) {
        this.page = 1;
        this.items = [];
        this.hasMore = false;
      }

      const searchHandle = api.qrCheckin.searchCodes({
        workplaceId: this.workplaceId,
        name: this.pattern.trim(),
        start: (this.page - 1) * PAGE_SIZE,
        pageSize: PAGE_SIZE,
      });

      this.searchHandle = searchHandle;

      try {
        const {
          data: {
            data,
            recordsFiltered,
            totalRecords,
          },
        } = await searchHandle;

        const rows = data || [];

        this.total = totalRecords || recordsFiltered || 0;
        this.hasMore = rows.length === PAGE_SIZE;
        this.items = reset ? rows : this.items.concat(rows);
      } catch (e) {
        if (axios.isCancel(e)) {
          return;
        }

        if (!reset) {
          this.page = Math.max(1, this.page - 1);
        }
      } finally {
        if (this.searchHandle === searchHandle) {
          this.searchHandle = null;
          this.loading[entity] = false;
          this.$nextTick(this.bindListScroll);
        }
      }
    },
  },

  beforeDestroy() {
    if (this.searchHandle) {
      this.searchHandle.cancel();
    }

    if (this.listEl) {
      this.listEl.removeEventListener('scroll', this.onListScroll);
    }
  },
};
</script>

<style lang="scss">
.location-qr-codes-download-modal {
  .modal-alert__body {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
    padding: 10px 10px;
  }

  &.modal-dialog-scrollable {
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: auto;
    min-height: calc(100% - 3.5rem);
    max-height: calc(100vh - 3.5rem);
    margin: 1.75rem auto;
    .modal-content {
      width: 100%;
      max-height: 100%;
    }
    .modal-body.modal-body__scrollable {
      display: flex;
      flex-direction: column;
      min-height: 0;
      overflow: hidden;
    }
  }

  &__list {
    flex: 1 1 auto;
    min-height: 0;
    max-height: 280px;
    overflow-y: auto;
  }

  &__list--loading {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding-right: 24px;
    margin-bottom: 12px;
  }

  &__checkbox {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex: 1;
    min-width: 0;
    margin-bottom: 0;

    .form-check-label {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      width: 100%;
      margin-right: auto;
    }
  }

  &__name {
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
  }

  &__preview {
    min-width: 64px;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #808080;
    cursor: pointer;
    transition: color 0.2s ease;

    svg {
      opacity: 0.55;
      transition: opacity 0.2s ease;
    }

    span {
      opacity: 0;
      transition: opacity 0.2s ease;
    }

    &:hover {
      color: #000;

      svg {
        opacity: 1;
      }

      span {
        opacity: 1;
      }
    }

    &:focus {
      outline: none;
    }

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    padding: 16px 0 24px;
  }
}
</style>
